import { DataLayerEventName, isIE, toRadioEvent } from '@news-mono/web-common'
import React, { useContext, useEffect, useState } from 'react'
import { Paragraph } from '../../../publication/Paragraph/Paragraph'
import { Subhead } from '../../../typography/Subhead/Subhead'
import { PlayerSponsorship } from '../LivePlayer/Sponsorship/Sponsorship'
import { TheWestLivePlayer } from '../TheWestLivePlayer/TheWestLivePlayer'
import { LivePlayerChunky } from '../TheWestLivePlayer/TheWestLivePlayerSkin/TheWestLivePlayerSkin'
import {
    TheWestLiveContext,
    useInProgrammedScheduleWindow,
    useOnAirState,
} from '../TheWestLiveProvider'
import {
    Styledheading,
    StyledIntroSection,
    StyledParagraph,
    StyledPlayerBox,
    StyledPlayerBranding,
    StyledPlayerContainer,
    StyledPlayerInfo,
    StyledPodcastLogoLink,
    StyledPodcastLogos,
    StyledScheduleNotice,
    StyledSharing,
    StyledSponsoredLogoContainer,
    StyledWestLiveLogo,
    StyledWestLiveLogoIcon,
} from './TheWestLivePlayerPage.styled'

const westLiveLogoIcon = require('../../../logos/TheWestLiveLogo/the-west-live-logo-icon.svg')
const applePodcastBadge = require('./assets/applepodcast.png')
const googlePodcastBadge = require('./assets/googlepodcast.png')
const spotifyPodcastBadge = require('./assets/spotifypodcast.png')

export interface TheWestLivePlayerPageProps {
    shareUrl: string
    showSponsorLogo?: boolean
    adUnit?: JSX.Element
    onEvent: (event: any) => void
}

export const TheWestLivePlayerPage: React.FC<TheWestLivePlayerPageProps> = (
    props,
) => {
    const { onAir, broadcast } = useOnAirState()
    const inScheduledWindow = useInProgrammedScheduleWindow()
    const { playerState, setPlayerState } = useContext(TheWestLiveContext)
    const [showIEMessage, setShowIEMessage] = useState(false)

    // Had to do this inside an effect so it occurs after the initial render, otherwise it caused an SSR error
    useEffect(() => {
        setShowIEMessage(isIE(navigator))
    }, [])

    function handleControlClick() {
        setPlayerState({
            ...playerState,
            state: playerState.state === 'stopped' ? 'loading' : 'stopped',
        })

        props.onEvent(
            toRadioEvent(
                playerState.state === 'stopped'
                    ? DataLayerEventName.radioPlay
                    : DataLayerEventName.radioStop,
                broadcast,
                'page',
            ),
        )
    }

    return (
        <React.Fragment>
            <Styledheading text="The West Live" />
            <StyledIntroSection>
                <StyledWestLiveLogo />
                <StyledParagraph noMargin>
                    The West Live is your daily talk show hosted by The West
                    Australian’s Ben O'Shea. He’ll tell you the stories behind
                    the stories and break down what is happening in and around
                    Perth and WA. It’s live, it’s local and it’s the news made
                    for you, when you want it. Contact us at{' '}
                    <a href="mailto:thewestlive@wanews.com.au?subject=Contact%20from%20The%20West%20Live%20page">
                        thewestlive@wanews.com.au
                    </a>
                    .
                </StyledParagraph>
            </StyledIntroSection>
            <StyledPlayerContainer>
                <div>
                    {props.showSponsorLogo && (
                        <StyledSponsoredLogoContainer>
                            <PlayerSponsorship />
                        </StyledSponsoredLogoContainer>
                    )}
                    <StyledPlayerBranding>
                        <StyledWestLiveLogoIcon
                            alt="The West Live logo"
                            src={westLiveLogoIcon}
                        />
                    </StyledPlayerBranding>
                    <StyledPlayerInfo>
                        <StyledParagraph>
                            The West Live, local Perth and WA news headlines
                            plus the stories behind the stories, every weekday
                            from 8:45am.{' '}
                            {showIEMessage
                                ? 'Live streams are not available on IE 11 but you can listen to previous episodes below.'
                                : 'Stream live here or catch up on the podcast.'}
                        </StyledParagraph>
                        <StyledPlayerBox>
                            {inScheduledWindow || onAir ? (
                                <TheWestLivePlayer
                                    PlayerSkin={(props) => (
                                        <LivePlayerChunky
                                            {...props}
                                            onControlClick={handleControlClick}
                                        />
                                    )}
                                />
                            ) : (
                                <StyledScheduleNotice>
                                    <strong>Weekdays</strong> <br /> 8.45am -
                                    9.30am
                                </StyledScheduleNotice>
                            )}
                        </StyledPlayerBox>
                        <StyledSharing
                            url={props.shareUrl}
                            text={
                                'Check out the latest episode for The West Live'
                            }
                            onEvent={props.onEvent}
                            shareOptions={[
                                'facebook',
                                'twitter',
                                'email',
                                'clipboard',
                            ]}
                        />
                    </StyledPlayerInfo>
                </div>
            </StyledPlayerContainer>

            {props.adUnit}

            <Subhead
                idAttribute="podcast"
                tag="h2"
                text="Subscribe to The West Live Podcast"
                onEvent={props.onEvent}
            />
            <Paragraph
                text="Never miss an episode by subscribing to the podcast - released each weekday after the live morning show."
                onEvent={props.onEvent}
            />
            <StyledPodcastLogoLink
                to="https://podcasts.apple.com/au/podcast/the-west-live-podcast/id1501795096?mt=2"
                rel="noopener"
            >
                <StyledPodcastLogos
                    src={applePodcastBadge}
                    alt="Listen to The West Live on Apple Podcasts"
                    width="136"
                    height="33"
                />
            </StyledPodcastLogoLink>
            <StyledPodcastLogoLink
                to="https://podcasts.google.com/?feed=aHR0cHM6Ly93d3cub21ueWNvbnRlbnQuY29tL2QvcGxheWxpc3QvMTg4NGYyNTgtMTdhZi00OTJkLWE4OTEtYTczZTAwNzIxMzIyLzJlNGJiZTg0LThjMzgtNDg4NC05MGRjLWFiNzQwMDhkMTcyNi82YWVhODZlYS00Y2Y0LTQzYjctYWQ0YS1hYjc0MDA4ZDliZWQvcG9kY2FzdC5yc3M"
                rel="noopener"
            >
                <StyledPodcastLogos
                    src={googlePodcastBadge}
                    alt="Listen to The West Live on Google Podcasts"
                    width="134"
                    height="34"
                />
            </StyledPodcastLogoLink>
            <StyledPodcastLogoLink
                to="https://open.spotify.com/show/5Mid5t7t2KPEczOTXAwnY7"
                rel="noopener"
            >
                <StyledPodcastLogos
                    src={spotifyPodcastBadge}
                    alt="Listen to The West Live on Spotify"
                    width="136"
                    height="33"
                />
            </StyledPodcastLogoLink>
        </React.Fragment>
    )
}

TheWestLivePlayerPage.displayName = 'TheWestLivePlayerPage'

export default TheWestLivePlayerPage
